import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { BarChart3, Bot, Calculator, Factory, LayoutDashboard, LogOut, Package, Radar, RotateCcw } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Analytics" },
  { to: "/admin/trends", icon: Radar, label: "Trend Discovery" },
  { to: "/admin/pricing", icon: Calculator, label: "Pricing Engine" },
  { to: "/admin/products", icon: Package, label: "Products & Channels" },
  { to: "/admin/orders", icon: BarChart3, label: "Orders" },
  { to: "/admin/returns", icon: RotateCcw, label: "Returns (RMA)" },
  { to: "/admin/suppliers", icon: Factory, label: "Manufacturers" },
  { to: "/admin/escalations", icon: Bot, label: "Chat Escalations" },
];

export default function AdminLayout() {
  const { admin, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0C]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
      </div>
    );
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="flex min-h-screen bg-[#0A0A0C]" data-testid="admin-layout">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-white/[0.08] bg-[#0D0D11] p-5 md:flex">
        <div className="flex items-center gap-2 px-2">
          <img src="/logo192.png" alt="TrendDrop logo" className="h-8 w-8 rounded-lg" />
          <span className="font-heading text-lg font-bold text-white">Trend<span className="text-amber-400">Drop</span></span>
        </div>
        <p className="mt-1 px-2 text-[10px] font-mono uppercase tracking-widest text-slate-600">Owner OS</p>
        <nav className="mt-8 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-amber-400/10 text-amber-300" : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto">
          <button
            data-testid="admin-view-store"
            onClick={() => navigate("/")}
            className="mb-2 w-full rounded-xl border border-white/10 px-3 py-2 text-sm text-slate-400 hover:text-white"
          >
            View storefront
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-500 hover:text-rose-400"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <div className="border-b border-white/[0.08] px-6 py-4 md:hidden">
          <div className="flex flex-wrap gap-2">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300">
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
